/**
 * WebSocket Service
 * 
 * Service for managing WebSocket connections and signaling for WebRTC
 */

import { appConfig } from '../config';
import { roomStore } from '../stores/roomStore';

export interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

export interface WebSocketServiceConfig {
  onMessage: (message: WebSocketMessage) => void;
  onOpen?: () => void;
  onError?: (error: Event) => void;
  onClose?: () => void;
}

export class ServerWebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketServiceConfig;
  private reconnectAttempts = 0;
  private reconnectTimeout: NodeJS.Timeout | null = null;

  constructor(config: WebSocketServiceConfig) {
    this.config = config;
  }

  /**
   * Connect to the WebSocket server
   */
  connect(): void {
    const wsUrl = appConfig.serverUrl.replace(/^http/, 'ws');
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      
      // Register with the server
      this.send({
        type: 'register',
        participantId: roomStore.participantId,
        roomId: roomStore.roomId,
      });

      if (this.config.onOpen) {
        this.config.onOpen();
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.config.onMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      if (this.config.onError) {
        this.config.onError(error);
      }
    };

    this.ws.onclose = () => {
      console.log('WebSocket closed');
      if (this.config.onClose) {
        this.config.onClose();
      }
    };
  }

  /**
   * Send a message through the WebSocket
   */
  send(message: WebSocketMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not open. Cannot send message:', message);
    }
  }

  /**
   * Check if the WebSocket is open
   */
  isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Close the WebSocket connection
   */
  close(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Reconnect to the WebSocket with retry logic
   */
  reconnect(onMaxAttemptsReached?: (attempts: number) => void): void {
    if (this.reconnectAttempts >= appConfig.webrtcRetryAttempts) {
      if (onMaxAttemptsReached) {
        onMaxAttemptsReached(this.reconnectAttempts);
      }
      return;
    }

    this.reconnectAttempts += 1;
    console.log(`Reconnecting... Attempt ${this.reconnectAttempts} of ${appConfig.webrtcRetryAttempts}`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, appConfig.webrtcRetryDelaySeconds * 1000);
  }
}
